import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AUTH_CONTEXT, ProjectNewComponent, ProjectNewSection } from '@luzula/luzula-lib';
import { VERFAHREN_WAHLEN, VerfahrenWahl, schreibeVerfahrenWahl, verfahrenWahlVon } from './verfahren';
import { kaAufnahmeWaehlbar } from './ka-schalter';

/**
 * Abschnitt "Kartierverfahren" im Fenster "Neues Projekt".
 *
 * Das Verfahren wird BEIM ANLEGEN festgelegt und ist danach fest: Der Abschnitt
 * ist mit `nurBeimAnlegen` angemeldet ({@link VERFAHREN_SECTION}) und erscheint im
 * Bearbeiten-Fenster gar nicht. Ein Auftrag läuft nach einem Verfahren; ein Projekt,
 * dessen Verfahren mittendrin wechselt, hätte Punkte beider Arten und ließe sich
 * weder auswerten noch prüfen noch drucken.
 *
 * Solange die KA-Aufnahme im Bau ist, sehen den Abschnitt nur Entwickler
 * (`sichtbar`, Regel in `ka-schalter.ts`); alle anderen legen SEA-95-Projekte an.
 * Ist sie fertig, gibt es dafür KEIN eigenes Recht mehr: Wer ein Projekt anlegen
 * darf, wählt dabei das Verfahren, so wie er den Namen wählt. An der
 * Berechtigungs-Matrix ändert sich dadurch nichts.
 *
 * Geschrieben wird direkt am Projekt-Objekt des Fensters; beim Anlegen speichert
 * das Fenster das ganze Objekt (siehe `PROJECT_NEW_SECTIONS` in der Bibliothek).
 */
@Component({
	selector: 'bp-verfahren-section',
	standalone: true,
	imports: [FormsModule],
	template: `
		<div class="bp-verfahren-wahl">
			@for (w of wahlen; track w.wahl) {
				<label class="form-check bp-verfahren-eintrag">
					<input class="form-check-input" type="radio" name="bpKartierverfahren"
						[value]="w.wahl" [(ngModel)]="wahl" [attr.data-testid]="'verfahren-' + w.wahl">
					<span class="bp-verfahren-text">
						<span class="fw-semibold">{{ w.name }}</span>
						<span class="small text-body-secondary">{{ w.erklaerung }}</span>
					</span>
				</label>
			}
			<div class="small text-body-secondary" data-testid="verfahren-hinweis">
				Das Kartierverfahren gilt für alle Punkte des Projekts und lässt sich nach dem Anlegen nicht mehr ändern.
			</div>
		</div>
	`,
	// Abstände über Stil, nicht über getippte Leerzeichen: Angular entfernt die beim Bauen.
	styles: [`
		.bp-verfahren-wahl { display: flex; flex-direction: column; gap: 0.6rem; }
		.bp-verfahren-eintrag { display: flex; align-items: flex-start; gap: 0.5rem; margin: 0; cursor: pointer; }
		.bp-verfahren-eintrag .form-check-input { margin-left: 0; float: none; flex: none; }
		.bp-verfahren-text { display: flex; flex-direction: column; }
	`],
})
export class VerfahrenSectionComponent {
	private fenster = inject(ProjectNewComponent);

	readonly wahlen = VERFAHREN_WAHLEN;

	private get projekt(): Record<string, any> | undefined {
		return this.fenster?.project;
	}

	get wahl(): VerfahrenWahl {
		return verfahrenWahlVon(this.projekt) ?? 'sea95';
	}
	set wahl(wert: VerfahrenWahl) {
		if (this.projekt) schreibeVerfahrenWahl(this.projekt, wert);
	}
}

/** Die Anmeldung des Abschnitts (für `projectNewSections` in `app-providers.ts`). */
export const VERFAHREN_SECTION: ProjectNewSection = {
	id: 'kartierverfahren',
	label: 'Kartierverfahren',
	component: VerfahrenSectionComponent,
	icon: 'bi-journal-check',
	order: 5,
	collapsed: false,
	// Nur beim Anlegen: Das Verfahren ist danach fest (siehe oben).
	nurBeimAnlegen: true,
	// Nur für Entwickler, solange die KA-Aufnahme im Bau ist (Regel in `ka-schalter.ts`).
	sichtbar: (injector) => kaAufnahmeWaehlbar(injector.get(AUTH_CONTEXT, null)),
};
